import { LoadingOutlined } from "@ant-design/icons"
import { FetchNextPageOptions, FetchStatus } from "@tanstack/react-query"
import { Form, Select } from "antd"
import { OptionProps } from "antd/lib/select"
import React, { useRef } from "react"
import { useDebouncedCallback } from "use-debounce"
import { DEBOUNCE_DURATION_MEDIUM } from "~Shared/config/consts"
import { getPopupAnchor } from "~Shared/utils/popup.utils"
import { FormProps } from "./form.types"

export interface InfiniteScrollSelectInterface<T> {
    dropdownData: Array<T> | undefined
    infiniteScrollData: {
        fetchNextPage: (options?: FetchNextPageOptions | undefined) => any
        hasNextPage: boolean | undefined
        isFetchingNextPage: boolean | undefined
        status: "error" | "loading" | "success"
        fetchStatus: FetchStatus
    }
    shouldApplyFilterOption: boolean
    isScrollable: boolean // Only needed for now as this component will be used for non-paginated data, such as employees. Once employees are moved to RQ, this will be removed
    disabled?: boolean
    search?: string
    placeholder?: string
    className?: string
    popupClassName?: string
    setSearchString: React.Dispatch<React.SetStateAction<string>>
    selectedItemId?: string
    isForm?: boolean
    isLoading?: boolean
    mode?: "tags" | "multiple"
    formProps?: FormProps
    renderSelectOptions: (item: T, index: number) => React.ReactElement | null
    renderSelectedOption: () => Array<React.ReactElement> | React.ReactElement | null
    renderFallbackSelectOption: () => React.ReactElement | null
    renderDefaultNonSelectedValue: () => React.ReactElement | null
    doFilterSelect?: (input: string, option: OptionProps) => boolean
    onSelect?: (value: string, option: OptionProps) => void
    onChange?: (item: string | Array<string>) => void
    dropdownRender?: (originNode: React.ReactElement) => React.ReactElement
}

export const InfiniteScrollSelect = <T,>({
    dropdownData,
    infiniteScrollData: { fetchNextPage, hasNextPage, isFetchingNextPage, status, fetchStatus },
    shouldApplyFilterOption,
    isScrollable = true,
    disabled,
    placeholder,
    className,
    popupClassName = "min-w-300",
    setSearchString,
    selectedItemId,
    isForm,
    isLoading,
    mode,
    formProps,
    doFilterSelect,
    onSelect,
    dropdownRender,
    renderSelectOptions,
    renderSelectedOption,
    renderFallbackSelectOption,
    renderDefaultNonSelectedValue,
    onChange,
}: InfiniteScrollSelectInterface<T>) => {
    const anchorRef = useRef(null)
    const isSelectLoading = isLoading || (status === "loading" && fetchStatus === "fetching")

    const handleScroll = (e: React.UIEvent<HTMLElement>) => {
        // Once having the employees moved to RQ we would not need the `isScrollable` prop
        if (isScrollable) {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
            if (scrollHeight - scrollTop <= clientHeight + 10) {
                if (hasNextPage && !isFetchingNextPage) {
                    fetchNextPage()
                }
            }
        }
    }

    const handleSearch = useDebouncedCallback((searchString) => setSearchString(searchString), DEBOUNCE_DURATION_MEDIUM)

    return (
        <>
            <div ref={anchorRef} />
            <Form.Item
                name={formProps?.name}
                initialValue={selectedItemId}
                style={formProps?.style ?? { marginBottom: "16px" }}
                label={formProps?.label}
                rules={formProps?.rules}
                extra={formProps?.extra}
                className={selectedItemId ? "active" : ""}
            >
                <Select
                    className={className}
                    popupClassName={popupClassName}
                    disabled={disabled}
                    mode={mode}
                    value={selectedItemId}
                    filterOption={shouldApplyFilterOption ? doFilterSelect : false}
                    onSelect={(itemId, option) => onSelect?.(itemId, option)}
                    onPopupScroll={handleScroll}
                    loading={isSelectLoading}
                    onSearch={(searchValue) => handleSearch.callback(searchValue)}
                    showSearch
                    placeholder={placeholder}
                    getPopupContainer={getPopupAnchor(anchorRef.current, isForm)}
                    dropdownRender={dropdownRender}
                    onChange={onChange}
                    data-testid="finway:workflow-trigger:object-select"
                >
                    {renderFallbackSelectOption()}
                    {renderSelectedOption()}
                    {renderDefaultNonSelectedValue()}
                    {dropdownData?.map((item: T, index: number) => renderSelectOptions(item, index))}
                    {fetchStatus === "fetching" && (
                        <div className="w-full text-center">
                            <LoadingOutlined />
                        </div>
                    )}
                </Select>
            </Form.Item>
        </>
    )
}
